/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { withPreference } from '@bingads-webui-react/preference-context';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { useWebsiteSuggestion } from '@bingads-webui-campaign-react/add-image-hooks';

import { ImageExtensionsPaneView } from './image-extensions-pane-view';
import {
  createImageExtensionMtModel,
  extractImageExtensionCreationMtFields,
  getImageExtensionItemThumbnailUrl,
  isImageExtensionCommitedToServer,
  getImagesFromImageList,
} from './components/index';

const decorateUIState = (items, { selectedIndex }) => _.map(items, (item, index) => ({
  ...item,
  selected: index === selectedIndex,
}));

const computeNewItemsMtModels = items => _.chain(items)
  .filter(item => !isImageExtensionCommitedToServer(item))
  .map(item => extractImageExtensionCreationMtFields(item))
  .value();

const ImageExtensionsPaneContainerBase = ({
  imageExtRepository,
  adContext,
  oDataConfig,
  instrumentation,
  permissions,
  fullUrl,
  hasErrorInUrl,
  adCreatorScenario,
  logActivity,
  headlines,
  descriptions,
  maxImageExtensionsPerAdGroup,
  onImageExtensionChanged,
  onPreviewImageChanged,
  preferenceService,
  cachedImageExtensionChanges,
  assetLibraryEventHandler,
  dataModelUtil,
  ...rest
}) => {
  const {
    suggestionState,
    dispatchSuggestionState,
    suggestionPoller,
  } = useWebsiteSuggestion({
    oDataConfig,
    adContext,
    instrumentation,
    logActivity,
    permissions,
    fullUrl,
    hasErrorInUrl,
    headlines,
    descriptions,
    adCreatorScenario,
  });
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState(cachedImageExtensionChanges || {
    current: [],
    added: [],
    removed: [],
    updated: [],
  });

  useEffect(() => {
    setLoading(true);

    imageExtRepository.association
      .findAll({
        where: { adGroupId: adContext.adGroupId, campaignId: adContext.campaignId },
        limit: maxImageExtensionsPerAdGroup,
        query: { $orderby: 'Id desc' },
      })
      .then((data) => {
        setChanges(prev => ({
          ...prev,
          current: decorateUIState([
            ...data,
            ...(cachedImageExtensionChanges && _.isArray(cachedImageExtensionChanges.current) ? cachedImageExtensionChanges.current : []),
          ], { selectedIndex: 0 }),
        }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [adContext.adGroupId, adContext.campaignId, imageExtRepository.association, maxImageExtensionsPerAdGroup]);

  useEffect(() => {
    onImageExtensionChanged(changes);
  }, [changes, onImageExtensionChanged]);

  useEffect(() => {
    const imageExtensionForPreview = _.find(changes.current, item => item.selected);
    if (permissions && permissions.dynamic.RSAImageExtensionCrop) {
      if (imageExtensionForPreview && dataModelUtil) {
        dataModelUtil.getImageListFromMTImagesAsync(imageExtensionForPreview.Images).then((imageList) => {
          const previewImage = _.find(imageList, image => image.type === 'Image178x100');
          onPreviewImageChanged(previewImage);
        });
      }
    } else {
      const previewUrl = imageExtensionForPreview && getImageExtensionItemThumbnailUrl(imageExtensionForPreview);
      onPreviewImageChanged({ src: previewUrl });
    }
  }, [changes, onPreviewImageChanged]);

  const createImageExtension = (fieldValues, selectedImage) => {
    const extensionData = createImageExtensionMtModel(fieldValues, selectedImage);
    const itemState = _.defaults({
      source: selectedImage[0].source,
    }, extensionData);

    setChanges(prev => ({
      ...prev,
      current: decorateUIState(
        [itemState, ...prev.current],
        { selectedIndex: 0 }
      ),
      added: [
        ...prev.added,
        extensionData,
      ],
    }));
  };

  const updateImageExtension = (index, selectedImage) => {
    const isItemAssociated = isImageExtensionCommitedToServer(changes.current[index]);
    const newCurrent = cloneDeep(changes.current);
    newCurrent[index].Images = getImagesFromImageList(selectedImage);
    if (isItemAssociated) {
      const newUpdateItem = _.filter(newCurrent, item => item.Id === newCurrent[index].Id);
      setChanges(prev => ({
        ...prev,
        current: decorateUIState(
          newCurrent,
          { selectedIndex: 0 }
        ),
        updated: [..._.filter(prev.updated, item => item.Id !== newCurrent[index].Id), ...newUpdateItem],
      }));
    } else {
      setChanges(prev => ({
        ...prev,
        current: decorateUIState(
          newCurrent,
          { selectedIndex: 0 }
        ),
        added: computeNewItemsMtModels(newCurrent),
      }));
    }
  };


  const deleteImageExtension = (item) => {
    const isItemAssociated = isImageExtensionCommitedToServer(item);

    setChanges((prev) => {
      const current = decorateUIState(
        _.without(prev.current, item),
        { selectedIndex: 0 }
      );

      return {
        ...prev,
        current,
        added: isItemAssociated ? prev.added : computeNewItemsMtModels(current),
        removed: isItemAssociated ? [...prev.removed, item] : prev.removed,
      };
    });
  };

  const onImgExtensionSelected = (index) => {
    setChanges(prev => ({
      ...prev,
      current: decorateUIState(prev.current, { selectedIndex: index }),
    }));
  };

  return (
    <div className="image-ext-pane-container">
      <ImageExtensionsPaneView
        loading={loading}
        setLoading={setLoading}
        selectedImgExtensionsForAssociation={changes.current}
        onImgExtensionSelected={onImgExtensionSelected}
        advertiserCustomerId={adContext.advertiserCustomerId}
        accountId={adContext.accountId}
        createImageExtension={createImageExtension}
        deleteImageExtension={deleteImageExtension}
        updateImageExtension={updateImageExtension}
        suggestionState={suggestionState}
        dispatchSuggestionState={dispatchSuggestionState}
        suggestionPoller={suggestionPoller}
        adContext={adContext}
        oDataConfig={oDataConfig}
        instrumentation={instrumentation}
        permissions={permissions}
        fullUrl={fullUrl}
        adCreatorScenario={adCreatorScenario}
        maxImageExtensionsPerAdGroup={maxImageExtensionsPerAdGroup}
        preferenceService={preferenceService}
        logActivity={logActivity}
        assetLibraryEventHandler={assetLibraryEventHandler}
        dataModelUtil={dataModelUtil}
        {...rest}
      />
    </div>
  );
};

ImageExtensionsPaneContainerBase.propTypes = {
  imageExtRepository: PropTypes.shape({}).isRequired,
  adContext: PropTypes.shape({
    campaignId: PropTypes.number,
    adGroupId: PropTypes.number,
    advertiserCustomerId: PropTypes.number,
    accountId: PropTypes.number,
  }).isRequired,
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }).isRequired,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
  adCreatorScenario: PropTypes.shape({
    scenarioName: PropTypes.string,
    scenarioValue: PropTypes.string,
  }),
  logActivity: PropTypes.func,
  permissions: PropTypes.shape({}).isRequired,
  fullUrl: PropTypes.string,
  headlines: PropTypes.arrayOf(PropTypes.string),
  descriptions: PropTypes.arrayOf(PropTypes.string),
  hasErrorInUrl: PropTypes.bool,
  maxImageExtensionsPerAdGroup: PropTypes.number,
  onImageExtensionChanged: PropTypes.func,
  onPreviewImageChanged: PropTypes.func,
  preferenceService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }).isRequired,
  activityLogger: PropTypes.shape({}),
  assetLibraryEventHandler: PropTypes.shape({}),
  dataModelUtil: PropTypes.shape({}),
};

ImageExtensionsPaneContainerBase.defaultProps = {
  adCreatorScenario: undefined,
  logActivity: _.noop,
  fullUrl: '',
  hasErrorInUrl: false,
  headlines: [],
  descriptions: [],
  maxImageExtensionsPerAdGroup: 0,
  onImageExtensionChanged: _.noop,
  onPreviewImageChanged: _.noop,
  activityLogger: {},
  assetLibraryEventHandler: {},
  dataModelUtil: {},
};

export const ImageExtensionsPaneContainer =
  React.memo(withPreference(ImageExtensionsPaneContainerBase));
